import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Fuel, Cog, Calendar, BadgeCheck, Share2, Flag, Phone, Mail, Loader2, Copy, Check } from 'lucide-react';
import api from '@/lib/axios';
import { resolveImageUrl, listingUrl } from '@/lib/listing';
import { useSeo } from '../hooks/useSeo';
import { track } from '@/lib/analytics';
import ListingContactForm from '@/components/marketplace/ListingContactForm';
import ConversationStartForm from '@/components/marketplace/ConversationStartForm';
import { useConversationsEnabled } from '@/lib/conversations';
import ReportListingModal from '@/components/marketplace/ReportListingModal';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const SITE = 'https://marketplacetvde.pt';

function shortFromParam(slugId) {
  return (slugId || '').split('-').pop();
}

function priceOf(item) {
  const d = item?.detalhes || {};
  if (item?.tipo === 'viatura') return d.valor_semanal;
  if (item?.tipo === 'venda') return d.preco_venda;
  return d.valor_aluguer_maximo;
}

export default function MarketplaceListingPage() {
  const { slugId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [activePhoto, setActivePhoto] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sent, setSent] = useState(false);
  const conversasAtivas = useConversationsEnabled();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/marketplace/anuncio/${shortFromParam(slugId)}`)
      .then((r) => { if (alive) { setData(r.data); setActivePhoto(0); if (r.data?.listing) track('view_vehicle', { publicacao_id: r.data.listing.id, tipo: r.data.listing.tipo }); } })
      .catch((e) => { if (alive) setErr(e.response?.status === 404 ? 'not_found' : 'error'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slugId]);

  const listing = data?.listing;
  const disponivel = listing?.disponivel;
  const price = priceOf(listing);
  const fotos = listing?.detalhes?.fotos || [];
  const canonical = listing ? `${SITE}${listingUrl(listing)}` : `${SITE}/`;
  const isVenda = listing?.tipo === 'venda';
  const isCar = listing?.tipo === 'viatura' || isVenda;
  const seoTitle = listing
    ? `${listing.titulo || (isCar ? 'Viatura TVDE' : 'Motorista TVDE')}${listing.cidade ? ' em ' + listing.cidade : ''} – Marketplace TVDE`
    : 'Anúncio – Marketplace TVDE';
  const seoDesc = listing
    ? `${isCar ? 'Viatura' : 'Motorista'} TVDE${listing.cidade ? ' em ' + listing.cidade : ''}. ${listing.descricao || listing.detalhes?.descricao || 'Veja os detalhes e contacte pelo Marketplace TVDE.'}`.slice(0, 160)
    : 'Anúncio do Marketplace TVDE.';

  useSeo({ title: seoTitle, description: seoDesc, path: listing ? listingUrl(listing) : '/' });

  // JSON-LD por anúncio
  useEffect(() => {
    if (!listing || !disponivel) return;
    const ld = {
      '@context': 'https://schema.org',
      '@type': isCar ? 'Product' : 'Service',
      name: listing.titulo || (isCar ? 'Viatura TVDE' : 'Motorista TVDE'),
      description: seoDesc,
      image: fotos.slice(0, 3).map(resolveImageUrl),
      areaServed: listing.cidade,
      url: canonical,
    };
    if (isCar && price) {
      ld.offers = { '@type': 'Offer', price: String(price), priceCurrency: 'EUR', availability: 'https://schema.org/InStock', url: canonical };
      if (listing.detalhes?.marca) ld.brand = { '@type': 'Brand', name: listing.detalhes.marca };
    }
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'listing-jsonld';
    el.text = JSON.stringify(ld);
    document.getElementById('listing-jsonld')?.remove();
    document.head.appendChild(el);
    return () => { document.getElementById('listing-jsonld')?.remove(); };
  }, [listing, disponivel, isCar, price, canonical, seoDesc, fotos]);

  const share = (net) => {
    const url = canonical;
    track('share_listing', { publicacao_id: listing?.id, network: net });
    const text = encodeURIComponent(`${listing?.titulo || 'Anúncio TVDE'} – Marketplace TVDE`);
    const u = encodeURIComponent(url);
    const links = {
      whatsapp: `https://wa.me/?text=${text}%20${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      email: `mailto:?subject=${text}&body=${u}`,
    };
    if (net === 'copy') {
      navigator.clipboard?.writeText(url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
      return;
    }
    window.open(links[net], '_blank', 'noopener');
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> A carregar anúncio...</div>;
  }
  if (err === 'not_found' || !listing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 px-4 text-center" data-testid="listing-notfound">
        <p className="text-lg font-bold text-slate-900">Anúncio não encontrado</p>
        <Link to="/" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white">Voltar ao Marketplace</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="listing-page">
      <div className="mx-auto max-w-5xl px-4 py-8 md:px-6">
        {/* Breadcrumbs */}
        <nav className="mb-4 flex items-center gap-1.5 text-xs text-slate-500" data-testid="listing-breadcrumbs">
          <Link to="/" className="hover:text-emerald-600">Início</Link>
          <span>/</span>
          <Link to="/" className="hover:text-emerald-600">{isCar ? 'Viaturas' : 'Motoristas'}</Link>
          <span>/</span>
          <span className="text-slate-700">{listing.titulo || listing.cidade}</span>
        </nav>

        <Link to="/" className="mb-4 inline-flex items-center gap-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700">
          <ArrowLeft className="h-3 w-3" /> Voltar
        </Link>

        {!disponivel ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center" data-testid="listing-unavailable">
            <p className="text-lg font-bold text-amber-800">Anúncio indisponível ou expirado</p>
            <p className="mt-1 text-sm text-amber-700">Este anúncio já não está ativo. Veja abaixo anúncios semelhantes.</p>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            {/* Coluna esquerda: galeria + detalhes */}
            <div>
              {fotos.length > 0 ? (
                <div>
                  <div className="aspect-video overflow-hidden rounded-2xl border border-slate-200 bg-white">
                    <img src={resolveImageUrl(fotos[activePhoto])} alt={listing.titulo} className="h-full w-full object-cover" data-testid="listing-main-photo" />
                  </div>
                  {fotos.length > 1 && (
                    <div className="mt-2 flex gap-2 overflow-x-auto">
                      {fotos.map((f, i) => (
                        <button key={i} onClick={() => setActivePhoto(i)} className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === activePhoto ? 'border-emerald-500' : 'border-transparent'}`} data-testid={`listing-thumb-${i}`}>
                          <img src={resolveImageUrl(f)} alt="" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex aspect-video items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-400">Sem fotografias</div>
              )}

              <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-2 flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white" title="Analisado pela equipa antes da publicação. Não é garantia da viatura.">
                    <BadgeCheck className="h-3 w-3" /> Anúncio revisto
                  </span>
                  {listing.contacto?.verificado && <span className="rounded-md bg-teal-600 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="listing-contact-confirmed">Contacto confirmado</span>}
                </div>
                <h1 className="text-2xl font-extrabold text-slate-900" data-testid="listing-title">{listing.titulo || (isCar ? 'Viatura TVDE' : 'Motorista TVDE')}</h1>
                <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-4 w-4 text-emerald-500" /> {listing.cidade || '—'}</p>
                {listing.contacto?.nome_publico && (
                  <p className="mt-1 text-sm text-slate-500" data-testid="listing-public-name">
                    {isCar ? 'Anunciado por' : 'Publicado por'} <strong className="font-semibold text-slate-700">{listing.contacto.nome_publico}</strong>
                    {listing.identidade_publica?.email_verificado && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200" data-testid="listing-email-verified">Email verificado</span>
                    )}
                  </p>
                )}
                {price ? (
                  <p className={`mt-3 text-2xl font-extrabold ${isVenda ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="listing-price">
                    {price} € <span className="text-sm font-medium text-slate-500">{isVenda ? 'à venda' : isCar ? '/ semana' : '(máx. aluguer/semana)'}</span>
                  </p>
                ) : null}

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  {listing.detalhes?.marca && <div><span className="text-slate-400">Marca</span><br /><strong>{listing.detalhes.marca}</strong></div>}
                  {listing.detalhes?.modelo && <div><span className="text-slate-400">Modelo</span><br /><strong>{listing.detalhes.modelo}</strong></div>}
                  {listing.detalhes?.ano && <div><span className="text-slate-400">Ano</span><br /><strong>{listing.detalhes.ano}</strong></div>}
                  {isVenda && listing.detalhes?.quilometros ? <div><span className="text-slate-400">Quilómetros</span><br /><strong>{Number(listing.detalhes.quilometros).toLocaleString('pt-PT')} km</strong></div> : null}
                  {listing.detalhes?.combustivel && <div className="flex items-center gap-1"><Fuel className="h-4 w-4 text-slate-400" /> {listing.detalhes.combustivel}</div>}
                  {listing.detalhes?.caixa && <div className="flex items-center gap-1"><Cog className="h-4 w-4 text-slate-400" /> {listing.detalhes.caixa}</div>}
                  {listing.published_at && <div className="flex items-center gap-1"><Calendar className="h-4 w-4 text-slate-400" /> Publicado em {String(listing.published_at).slice(0, 10)}</div>}
                </div>

                {listing.identidade_publica?.telefone_verificado && (
                  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="listing-contact-note">
                    <strong>Contactos confirmados pelo Marketplace.</strong>{' '}
                    Confirmamos o email e/ou o telemóvel do anunciante. Não verificamos documentos de identidade
                    nem documentos da viatura, e isto não representa garantia mecânica ou contratual.
                  </div>
                )}

                {(listing.descricao || listing.detalhes?.descricao) && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-slate-600" data-testid="listing-description">{listing.descricao || listing.detalhes?.descricao}</p>}

                {/* Contactos públicos (só se autorizados) */}
                {(listing.contacto?.telefone || listing.contacto?.email) && (
                  <div className="mt-4 flex flex-wrap gap-3 border-t border-slate-100 pt-4 text-sm">
                    {listing.contacto?.telefone && <a href={`tel:${listing.contacto.telefone}`} className="inline-flex items-center gap-1 font-semibold text-emerald-600" data-testid="listing-public-phone"><Phone className="h-4 w-4" /> {listing.contacto.telefone}</a>}
                    {listing.contacto?.email && <a href={`mailto:${listing.contacto.email}`} className="inline-flex items-center gap-1 font-semibold text-emerald-600" data-testid="listing-public-email"><Mail className="h-4 w-4" /> {listing.contacto.email}</a>}
                  </div>
                )}

                {/* Partilha + denúncia */}
                <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
                  <span className="mr-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><Share2 className="h-4 w-4" /> Partilhar:</span>
                  <button onClick={() => share('whatsapp')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="share-whatsapp">WhatsApp</button>
                  <button onClick={() => share('facebook')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="share-facebook">Facebook</button>
                  <button onClick={() => share('email')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="share-email">Email</button>
                  <button onClick={() => share('copy')} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50" data-testid="share-copy">{copied ? <><Check className="h-3 w-3 text-emerald-600" /> Copiado</> : <><Copy className="h-3 w-3" /> Copiar link</>}</button>
                  <button onClick={() => setShowReport(true)} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid="listing-report-btn"><Flag className="h-3 w-3" /> Denunciar</button>
                </div>
              </div>
            </div>

            {/* Coluna direita: contacto */}
            <div>
              <div className="sticky top-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="listing-contact-box">
                <h2 className="mb-1 text-base font-bold text-slate-900">Enviar mensagem ao anunciante</h2>
                <p className="mb-4 text-xs text-slate-500">A conversa acontece dentro do Marketplace. Aviso: confirme sempre a documentação e veja a viatura presencialmente.</p>
                {conversasAtivas ? (
                  <ConversationStartForm listingId={listing.id} titulo={listing.titulo} />
                ) : sent ? (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800" data-testid="listing-contact-sent">Contacto enviado com sucesso. O anunciante irá responder-lhe.</div>
                ) : (
                  <ListingContactForm listingId={listing.id} onSent={() => { setSent(true); track('send_contact', { publicacao_id: listing.id, tipo: listing.tipo }); }} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Semelhantes */}
        {data?.similares?.length > 0 && (
          <div className="mt-10" data-testid="listing-similares">
            <h2 className="mb-4 text-lg font-bold text-slate-900">Anúncios semelhantes</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.similares.map((s) => (
                <Link key={s.id} to={listingUrl(s)} className="group overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:shadow-md" data-testid={`similar-${s.id}`}>
                  <div className="aspect-video overflow-hidden bg-slate-100">
                    {s.detalhes?.fotos?.[0] ? <img src={resolveImageUrl(s.detalhes.fotos[0])} alt={s.titulo} className="h-full w-full object-cover transition group-hover:scale-105" /> : <div className="flex h-full items-center justify-center text-slate-300">TVDE</div>}
                  </div>
                  <div className="p-3">
                    <p className="truncate text-sm font-bold text-slate-900">{s.titulo || s.cidade}</p>
                    <p className="text-xs text-slate-500">{s.cidade}</p>
                    {priceOf(s) ? <p className="mt-1 text-sm font-extrabold text-emerald-600">{priceOf(s)} €</p> : null}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {showReport && <ReportListingModal listingId={listing.id} onClose={() => setShowReport(false)} />}
      <MarketplaceFooter />
    </div>
  );
}
